import { UNAVAILABLE_REASONS } from "_shared/_api/by-ids/by-ids-result";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import BookmarkedTeam from "marketplace/bookmarks/team-bookmarks/bookmarked-team";
import BookmarkedTeamSchema from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import Team from "marketplace/teams/team";
import TeamSchema from "marketplace/teams/_schema/team-schema";
import { z } from "zod";

export type BookmarkedTeamListItemJSON = z.infer<typeof BookmarkedTeamListItemSchema.BookmarkedTeamListItemJSONZ>;
export type BookmarkedTeamListItemParams = z.infer<typeof BookmarkedTeamListItemSchema.BookmarkedTeamListItemZ>;

/**
 * A row of the list endpoint can only be refused, never missing: the bookmark exists, so the row
 * stays. It is module-private so that both public schemas can read it.
 */
const listItemUnavailableReasonZ = z.enum(UNAVAILABLE_REASONS).extract(["unauthorized"]);

/**
 * The team of a row. A hidden team may have no profile, and the caller may still read it as its
 * leader, a member or an invitee, so its name can be null here.
 */
const listItemTeamJSONZ = TeamSchema.TeamJSONZ.extend({ name: z.string().nullable() });

/**
 * One row of `GET marketplace/team-bookmarks`: the bookmark, and the team when the caller may
 * read it. When the caller may not, `team` is null and `unavailableReason` is "unauthorized".
 */
export default class BookmarkedTeamListItemSchema {
  public static readonly BookmarkedTeamListItemJSONZ = z.object({
    ...BookmarkedTeamSchema.BookmarkedTeamJSONZ.shape,
    team: listItemTeamJSONZ.nullable(),
    unavailableReason: listItemUnavailableReasonZ.nullable(),
  });
  public static readonly BookmarkedTeamListItemZ = z.object({
    bookmark: z.instanceof(BookmarkedTeam),
    team: z.instanceof(Team).optional(),
    unavailableReason: listItemUnavailableReasonZ.optional(),
  });

  public static validate(values: unknown): BookmarkedTeamListItemParams {
    return parseOrThrow(BookmarkedTeamListItemSchema.BookmarkedTeamListItemZ, values, {
      logError: true,
      schemaName: "BookmarkedTeamListItemSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkedTeamListItemJSON {
    return parseOrThrow(BookmarkedTeamListItemSchema.BookmarkedTeamListItemJSONZ, values, {
      logError: true,
      schemaName: "BookmarkedTeamListItemSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is BookmarkedTeamListItemJSON {
    try {
      BookmarkedTeamListItemSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }
}
